import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { dataRoomService } from '@/services/dataRoomService';
import { Project, ProjectUnlockData, ProjectGraphData } from '@/types/project';
import { PageTemplate } from '@/components/templates';
import { CONTAINER_SIZES } from '@/constants';
import { useIsMobile } from '@/hooks/use-mobile';

import { 
  FileText,
  Unlock, 
  BarChart3
} from 'lucide-react';
import { ProjectRFQModal } from '@/components/organisms/modals';

// Tab components
import ProjectOverview from './components/ProjectOverview';
import ProjectUnlocks from './components/ProjectUnlocks';
import ProjectUnlocksMobile from './components/ProjectUnlocksMobile';
import ProjectAnalytics from './components/ProjectAnalytics';
import ProjectDetailHeaderDesktop from './components/ProjectDetailHeaderDesktop';
import ProjectDetailHeaderMobile from './components/ProjectDetailHeaderMobile';

interface Tab {
  id: string;
  label: string;
  icon: React.ReactNode;
}

const tabs: Tab[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: <FileText size={12} />
  },
  {
    id: 'unlocks',
    label: 'Unlocks',
    icon: <Unlock size={12} />
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: <BarChart3 size={12} />
  }
];

const ProjectDetailScreen: React.FC = () => {
  const isMobile = useIsMobile();
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [project, setProject] = useState<Project | null>(null);
  const [unlockData, setUnlockData] = useState<ProjectUnlockData | null>(null);
  const [graphData, setGraphData] = useState<ProjectGraphData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showRFQModal, setShowRFQModal] = useState(false);

  // Set active tab based on URL path
  useEffect(() => {
    if (location.pathname.includes('/unlock')) {
      setActiveTab('unlocks');
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!projectId) {
      setError('Project ID is required');
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchProjectData = async () => {
      console.log('fetchProjectData started for projectId:', projectId);
      try {
        setLoading(true);
        setError(null);

        // Fetch project data with individual error handling
        let projectDetail, unlockResponse, graphResponse;
        
        try {
          projectDetail = await dataRoomService.getProjectDetail(projectId, true);
          if (isMounted) {
            setProject(projectDetail);
          }
        } catch (error) {
          console.error('Failed to fetch project details:', error);
          if (isMounted) {
            setError('Failed to load project details');
          }
          // Don't set loading here - let finally block handle it
          return;
        }
        
        // Only fetch unlock and graph data if project detail was successful
        if (!isMounted) return;
        
        try {
          unlockResponse = await dataRoomService.getProjectUnlocks(projectId);
          // getProjectUnlocks returns null for 404 errors, so this is safe
          if (isMounted) {
            if (unlockResponse) {
              setUnlockData(unlockResponse.unlock);
            } else {
              setUnlockData(null);
            }
          }
        } catch (error) {
          // This should rarely happen since 404s are handled in the service
          console.warn('Failed to fetch unlock data:', error);
          if (isMounted) {
            setUnlockData(null);
          }
        }
        
        if (!isMounted) {
          return;
        }
        
        try {
          graphResponse = await dataRoomService.getProjectGraph(projectId);
          
          if (isMounted) {
            // Ensure we have valid graph data before setting it
            if (graphResponse && typeof graphResponse === 'object') {
              setGraphData(graphResponse);
            } else {
              setGraphData(null);
            }
          }
        } catch (error) {
          // Don't fail the entire page if graph data is missing
          if (isMounted) {
            setGraphData(null);
          }
        }
      } catch (err: any) {
        console.error('Failed to fetch project data:', err);
        if (isMounted) {
          setError(err.message || 'Failed to load project data');
        }
      } finally {
        // Always set loading to false, regardless of mount state
        setLoading(false);
      }
    };

    fetchProjectData();
    
    return () => {
      isMounted = false;
    };
  }, [projectId]);

  // Switch to overview tab if user is on unlocks tab but unlock data is not available
  useEffect(() => {
    if (activeTab === 'unlocks' && !unlockData && !loading) {
      setActiveTab('overview');
    }
  }, [activeTab, unlockData, loading]);

  const handlePlaceInquiry = () => {
    if (project) {
      setSelectedProject(project);
      setShowRFQModal(true);
    }
  };

  const handleRFQSubmitted = () => {
    console.log('RFQ submitted successfully');
    setShowRFQModal(false);
    setSelectedProject(null);
  };

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      );
    }

    if (!project) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Project not found</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'overview':
        return <ProjectOverview project={project} loading={false} onPlaceInquiry={handlePlaceInquiry} />;
      case 'unlocks':
        return unlockData ? (isMobile ? <ProjectUnlocksMobile unlockData={unlockData} /> : <ProjectUnlocks unlockData={unlockData} />) : (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">No unlock data available for this project</p>
          </div>
        );
      case 'analytics':
        // Always pass graphData to ProjectAnalytics - it will handle null/undefined/empty data
        return <ProjectAnalytics graphData={graphData} projectId={projectId || ''} project={project} loading={false} />;
      default:
        return <ProjectOverview project={project} loading={false} onPlaceInquiry={handlePlaceInquiry} />;
    }
  };

  return (
    <PageTemplate maxWidth={CONTAINER_SIZES.WIDE}>
      <div className="space-y-6">
        {isMobile ? (
          <ProjectDetailHeaderMobile
            project={project}
            loading={loading}
            activeTab={activeTab}
            tabs={tabs}
            showUnlocksTab={!!unlockData}
            onTabChange={setActiveTab}
            onPlaceInquiry={handlePlaceInquiry}
          />
        ) : (
          <ProjectDetailHeaderDesktop
            project={project}
            loading={loading}
            activeTab={activeTab}
            tabs={tabs}
            showUnlocksTab={!!unlockData}
            onTabChange={setActiveTab}
            onPlaceInquiry={handlePlaceInquiry}
          />
        )}

        {/* Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="min-h-[600px]"
        >
          {renderTabContent()}
        </motion.div>
      </div>

      {/* RFQ Modal */}
      <ProjectRFQModal
        isOpen={showRFQModal}
        onClose={() => {
          setShowRFQModal(false);
          setSelectedProject(null);
        }}
        project={selectedProject}
        onRFQSubmitted={handleRFQSubmitted}
      />
    </PageTemplate>
  );
};

export default ProjectDetailScreen; 
